import ExcelJS from 'exceljs';

// Optional i18next-style translate function. When omitted, the German default
// is used verbatim (keeps the helpers pure and their unit tests language-free).
export type TFunc = (key: string, opts?: { defaultValue?: string; count?: number }) => string;

const tr = (t: TFunc | undefined, key: string, fallback: string, count?: number): string =>
    t ? t(key, { defaultValue: fallback, count }) : fallback;

// Range check (with rollover), noValidation exempts it. Shared by dataValidation, status, formatting.
function rangeOkFragment(valueColRef: string, minColRef: string, maxColRef: string, noValidationColRef: string): string {
  return (
    `OR(${noValidationColRef}=TRUE,` +
    `OR(` +
    `AND(${minColRef}<${maxColRef},${valueColRef}>=${minColRef},${valueColRef}<=${maxColRef}),` +
    `AND(${minColRef}>=${maxColRef},OR(${valueColRef}>=${minColRef},${valueColRef}<=${maxColRef}))` +
    `))`
  );
}

// Vorkommastellen-Prüfung (#60): der Wert darf das Zählwerk nicht überlaufen.
// Fehlt die Angabe (leere Zelle bzw. 0), entfällt die Prüfung.
function preDecimalsOkFragment(valueColRef: string, preDecimalsColRef: string): string {
  return `OR(${preDecimalsColRef}="",${preDecimalsColRef}=0,${valueColRef}<POWER(10,${preDecimalsColRef}))`;
}

// Bewusst KEINE Excel-Datenüberprüfung (dataValidation) auf Wert und Datum:
// Excel zeigt dafür bei jeder Eingabe ein Popup bzw. verweigert die Eingabe
// ganz ('stop'), was die Datei praktisch unbenutzbar macht. Rückmeldung gibt
// es nur passiv über die Status-Formel (Text) und die bedingten
// Formatierungen weiter unten. Die verbindliche Prüfung passiert beim Import.

export const STATUS_INCOMPLETE = 'Daten unvollständig';
export const STATUS_DONE = 'Ablesung erledigt';
export const STATUS_INVALID = 'Daten ungültig';       // nicht heilbar → korrigieren
export const STATUS_IMPLAUSIBLE = 'Zählerstand unplausibel'; // heilbar → Foto/Ablesehinweis beim Übermitteln
export const STATUS_ALREADY_READ = 'Bereits abgelesen';

interface StatusLabels {
  incomplete: string;
  done: string;
  invalid: string;
  implausible: string;
  alreadyRead: string;
}

// Resolves the status labels for the active language. Both the status
// formula and the conditional formatting must use the SAME labels within one
// file, so both call this with the same `t`.
function resolveStatusLabels(t?: TFunc): StatusLabels {
  return {
    incomplete: tr(t, 'management.excelExport.file.status.incomplete', STATUS_INCOMPLETE),
    done: tr(t, 'management.excelExport.file.status.done', STATUS_DONE),
    invalid: tr(t, 'management.excelExport.file.status.invalid', STATUS_INVALID),
    implausible: tr(t, 'management.excelExport.file.status.implausible', STATUS_IMPLAUSIBLE),
    alreadyRead: tr(t, 'management.excelExport.file.status.alreadyRead', STATUS_ALREADY_READ),
  };
}

/**
 * Status-Logik (column-driven, faithful an JobDetails):
 * - kein Wert                              → Ablesung unvollständig
 * - kein Wert + vorhandenes Portal-Ergebnis → Bereits abgelesen
 * - Nachkommastellen falsch (decimalsCheck) → Ablesung ungültig (nicht heilbar)
 * - Bereich verletzt: strict ? ungültig : unplausibel
 * - Historic/Continuous verletzt           → Ablesung unplausibel (nie hart, auch bei strict)
 * - sonst                                   → Ablesung erledigt
 * Ein leeres Ablesedatum blockiert den Status NICHT grundsätzlich: der Import
 * setzt es automatisch auf den Import-Tag (fillMissingReadingDate). Damit das
 * ehrlich bleibt, prüft die Formel per TODAY(), ob der heutige Tag im
 * erlaubten Ablesefenster (__minAllowedDate/__maxAllowedDate) liegt — sonst
 * würde der Import die Zeile als invalid_reading_date ablehnen und der Status
 * bleibt "unvollständig" (Datum erforderlich).
 * `strictColRef`, `decimalsCheckColRef`, `histCheckColRef`, `contCheckColRef` sind versteckte
 * Boolean-Spalten (per Zeile). last*ColRef leer ('') ⇒ Term entfällt (Spalte fehlt im Format).
 */
export function buildStatusFormula(colRefs: {
  valueColRef: string;
  dateColRef: string;
  minColRef: string;
  maxColRef: string;
  noValidationColRef: string;
  postDecimalsColRef: string;
  preDecimalsColRef?: string;
  strictColRef: string;
  decimalsCheckColRef: string;
  histCheckColRef: string;
  contCheckColRef: string;
  alreadyReadColRef?: string;
  lastReadingColRef: string;
  lastResultColRef: string;
  minAllowedColRef?: string;
  maxAllowedColRef?: string;
}, t?: TFunc): string {
  const {
    valueColRef, dateColRef, minColRef, maxColRef, noValidationColRef, postDecimalsColRef,
    preDecimalsColRef, strictColRef, decimalsCheckColRef, histCheckColRef, contCheckColRef,
    alreadyReadColRef, lastReadingColRef, lastResultColRef,
    minAllowedColRef, maxAllowedColRef,
  } = colRefs;

  const status = resolveStatusLabels(t);
  const rangeOk = rangeOkFragment(valueColRef, minColRef, maxColRef, noValidationColRef);
  const decFail = `AND(${decimalsCheckColRef}=TRUE,MOD(${valueColRef}*POWER(10,${postDecimalsColRef}),1)<>0)`;
  // Zählwerk-Überlauf (#60): zu viele Vorkommastellen sind nie heilbar.
  const preFail = preDecimalsColRef
    ? `AND(${preDecimalsColRef}<>"",${preDecimalsColRef}>0,${valueColRef}>=POWER(10,${preDecimalsColRef}))`
    : null;
  const histFail = lastReadingColRef
    ? `AND(${histCheckColRef}=TRUE,${lastReadingColRef}<>"",${valueColRef}<${lastReadingColRef})`
    : 'FALSE';
  const contFail = lastResultColRef
    ? `AND(${contCheckColRef}=TRUE,${lastResultColRef}<>"",${valueColRef}<${lastResultColRef})`
    : 'FALSE';

  const emptyStatus = alreadyReadColRef
    ? `IF(AND(${valueColRef}="",${alreadyReadColRef}=TRUE),"${status.alreadyRead}","${status.incomplete}")`
    : `"${status.incomplete}"`;

  // Wert ohne Datum: der Import ersetzt das Datum durch den Import-Tag. Liegt
  // "heute" außerhalb des erlaubten Fensters, würde der Import die Zeile
  // ablehnen — dann muss der Status ehrlich "unvollständig" bleiben.
  const dateMissingOutsideWindow = minAllowedColRef && maxAllowedColRef
    ? `AND(${dateColRef}="",OR(TODAY()<${minAllowedColRef},TODAY()>${maxAllowedColRef}))`
    : 'FALSE';

  return (
    `IF(${valueColRef}="",${emptyStatus},` +
    `IF(${dateMissingOutsideWindow},"${status.incomplete}",` +
    `IF(${decFail},"${status.invalid}",` +
    (preFail ? `IF(${preFail},"${status.invalid}",` : '') +
    `IF(NOT(${rangeOk}),IF(${strictColRef}=TRUE,"${status.invalid}","${status.implausible}"),` +
    `IF(OR(${histFail},${contFail}),"${status.implausible}",` +
    `"${status.done}")))))` +
    (preFail ? ')' : '')
  );
}

export interface ConditionalFormatRule {
  ref: string;
  rules: ExcelJS.ConditionalFormattingRule[];
}

// Status-Formatierung: nur Schriftfarbe (fett), keine Zellfüllungen — die
// Datei bleibt ruhig, der Status ist trotzdem auf einen Blick erkennbar.
// Leere Eingabezellen werden nicht mehr gelb markiert; der Eintragbereich ist
// über das Gruppenband und den Zellrahmen erkennbar.
export function buildConditionalFormats(colRefs: {
  statusRef: string;
  valueRef: string;
  dateRef: string;
}, t?: TFunc): ConditionalFormatRule[] {
  const { statusRef } = colRefs;

  const status = resolveStatusLabels(t);

  const statusRules: ConditionalFormatRule = {
    ref: statusRef,
    rules: [
      {
        type: 'cellIs',
        priority: 1,
        operator: 'equal',
        formulae: [`"${status.incomplete}"`],
        style: {
          font: { bold: true, color: { argb: 'FF666666' } },
        },
      },
      {
        type: 'cellIs',
        priority: 2,
        operator: 'equal',
        formulae: [`"${status.done}"`],
        style: {
          font: { bold: true, color: { argb: 'FF15803D' } },
        },
      },
      {
        type: 'cellIs',
        priority: 3,
        operator: 'equal',
        formulae: [`"${status.alreadyRead}"`],
        style: {
          font: { bold: true, color: { argb: 'FF1F4E79' } },
        },
      },
      {
        // heilbar (Foto/Ablesehinweis beim Übermitteln) — orange
        type: 'cellIs',
        priority: 4,
        operator: 'equal',
        formulae: [`"${status.implausible}"`],
        style: {
          font: { bold: true, color: { argb: 'FFCC5500' } },
        },
      },
      {
        // nicht heilbar (korrigieren) — kräftiges Rot
        type: 'cellIs',
        priority: 5,
        operator: 'equal',
        formulae: [`"${status.invalid}"`],
        style: {
          font: { bold: true, color: { argb: 'FFCC0000' } },
        },
      },
    ],
  };

  return [statusRules];
}

export interface CellValidationColRefs {
  valueRange: string;       // CF ref, e.g. "C2:C1000"
  dateRange: string;        // CF ref, e.g. "D2:D1000"
  valueFirstRef: string;    // first cell, relative, e.g. "C2"
  dateFirstRef: string;     // first cell, relative, e.g. "D2"
  minRef: string;           // abs-col, e.g. "$H2"
  maxRef: string;
  postDecimalsRef: string;
  preDecimalsRef?: string;  // abs-col — Vorkommastellen-Prüfung nur wenn vorhanden (#60)
  noValidationRef: string;
  decimalsCheckRef: string; // abs-col boolean — Nachkommastellen nur wenn TRUE
  histCheckRef: string;     // abs-col boolean — Historic-Prüfung nur wenn TRUE
  contCheckRef: string;     // abs-col boolean — Continuous-Prüfung nur wenn TRUE
  lastReadingRef?: string;  // relative ref to visible last-reading value (omit if column absent)
  lastResultRef?: string;   // relative ref to visible last-result value (omit if column absent)
  minAllowedRef: string;    // abs-col for __minAllowedDate
  maxAllowedRef: string;    // abs-col for __maxAllowedDate
}

export function buildCellValidationFormats(colRefs: CellValidationColRefs): ConditionalFormatRule[] {
  const {
    valueRange, dateRange,
    valueFirstRef, dateFirstRef,
    minRef, maxRef, postDecimalsRef, preDecimalsRef, noValidationRef,
    decimalsCheckRef, histCheckRef, contCheckRef, lastReadingRef, lastResultRef,
    minAllowedRef, maxAllowedRef,
  } = colRefs;

  const rangeOk = rangeOkFragment(valueFirstRef, minRef, maxRef, noValidationRef);
  const decOk = `OR(${decimalsCheckRef}<>TRUE,MOD(${valueFirstRef}*POWER(10,${postDecimalsRef}),1)=0)`;
  const okParts = [rangeOk, decOk];
  if (preDecimalsRef) okParts.push(preDecimalsOkFragment(valueFirstRef, preDecimalsRef));
  if (lastReadingRef) okParts.push(`OR(${histCheckRef}<>TRUE,${lastReadingRef}="",${valueFirstRef}>=${lastReadingRef})`);
  if (lastResultRef) okParts.push(`OR(${contCheckRef}<>TRUE,${lastResultRef}="",${valueFirstRef}>=${lastResultRef})`);

  const valueInvalidFormula =
    `AND(${valueFirstRef}<>"",` +
    `NOT(AND(${okParts.join(',')})))`;

  const dateInvalidFormula =
    `AND(${dateFirstRef}<>"",` +
    `NOT(AND(ISNUMBER(${dateFirstRef}),${dateFirstRef}>=${minAllowedRef},${dateFirstRef}<=${maxAllowedRef})))`;

  const invalidStyle: ExcelJS.ConditionalFormattingRule['style'] = {
    font: { color: { argb: 'FFCC0000' } },
    // bgColor mitsetzen — Apple Numbers rendert solide Füllungen ohne bgColor falsch (#55).
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFCCCC' }, bgColor: { argb: 'FFFFCCCC' } },
  };

  return [
    {
      ref: valueRange,
      rules: [{ type: 'expression', priority: 1, formulae: [valueInvalidFormula], style: invalidStyle }],
    },
    {
      ref: dateRange,
      rules: [{ type: 'expression', priority: 1, formulae: [dateInvalidFormula], style: invalidStyle }],
    },
  ];
}
